import axios from 'axios';
import { createApp } from 'vue';
import eventBus from '../../../_00_elements/tools/event-bus';
import Logger from '../../../_00_elements/tools/logger/logger';
import LoginComponent from './components/login-modal-component';
import LoginPreSetComponent from './components/login-pre-set-modal-component';
import RegisterChoiceComponent from './components/register-choice-modal-component';
import RegisterComponent from './components/register-modal-component';
import MissingEmailComponent from './components/missing-email-modal-component';
import DeleteComponent from './components/delete-modal-component';
import DeleteSuccessComponent from './components/delete-success-modal-component.vue';
import { UserOrigin, ORIGIN_SIMPLE } from '../../../_00_elements/tools/user/origin';
import ResettingPasswordComponent from './components/resetting-password-modal-component';
import LocalStorageAdapter from '../../../_00_elements/tools/storage/adapter/local-storage-adapter';
import Storage from '../../../_00_elements/tools/storage/storage';
import {
  URL_HEADER_MENU,
  URL_HEADER_MENU_DESKTOP,
} from '../../../_00_elements/tools/routes/routes';
import UserAuth from '../../../_00_elements/tools/user/auth';
import translation from '../../../_00_elements/translation';

export default function (VUE_ELEMENT) {
  function clickOnGoogleButton(button) {
    button.classList.add('disabled');
  }

  function initHeader() {
    const authUser = UserAuth.getUser();
    if (authUser) {
      // let app search time to start
      eventBus.addEventListener('search-initialized', () => {
        // this will be listened by search app to create alerts or init account manager picture
        eventBus.dispatchEvent(new CustomEvent('header-menu-user', { detail: authUser }));
      });
    }

    const component = { // eslint-disable-line no-new
      components: {
        'login-modal': LoginComponent,
        'login-pre-set-modal': LoginPreSetComponent,
        'register-choice-modal': RegisterChoiceComponent,
        'register-modal': RegisterComponent,
        'missing-email-modal': MissingEmailComponent,
        'delete-modal': DeleteComponent,
        'delete-success-modal': DeleteSuccessComponent,
        'resetting-password-modal': ResettingPasswordComponent,
      },
      mixins: [ translation ],
      data() {
        return {
          isLoginModalOpened: false,
          isLoginPreSetModalOpened: false,
          isRegisterChoiceModalOpened: false,
          isRegisterModalOpened: false,
          isMissingInformationModalOpened: false,
          isDeleteModalOpened: false,
          isDeleteSuccessModalOpened: false,
          isResettingPasswordModalOpened: false,
          presetEmailLogin: null,
          presetErrorLogin: null,
          presetSuccessLogin: null,
          redirectUrl: window.location.href,
        };
      },
      mounted() {
        const currentRoute = window.location.hash;
        if (currentRoute.indexOf('#missing_') === 0) {
          this.showMissingInformationModal();
        }

        const loginHeaderButton = document.querySelector('#gtm-header-connection');
        if (loginHeaderButton) {
          loginHeaderButton.addEventListener('click', (event) => {
            this.showLoginModal(event);
          });
          loginHeaderButton.setAttribute('data-ready', true);
        }

        if (currentRoute === '#login' && document.querySelector('#gtm-header-connection')) {
          const queryString = window.location.search;
          const urlParams = new URLSearchParams(queryString);
          let error = null;
          if (urlParams.get('activated') === '1') {
            const transKey = this.getLoginErrorAlreadyActiveTransKey(urlParams);
            error = this.trans(transKey);
          }
          this.showLoginModal(null, null, null, error);
        }

        if (currentRoute === '#login-pre-set') {
          this.showLoginPreSetModal();
        }

        if (currentRoute === '#register') {
          this.showRegisterChoiceModal();
        }

        eventBus.addEventListener('show-register-modal', (...args) => {
          this.showRegisterModal(...args);
        });

        eventBus.addEventListener('show-login-modal', (args) => {
          this.showLoginModal(args ? args.detail : null);
        });

        eventBus.addEventListener('show-delete-modal', (...args) => {
          this.showDeleteModal(...args);
        });

        eventBus.addEventListener('show-delete-success-modal', this.showDeleteSuccessModal);

        /**
         * This event must be called when you want to switch from a modal to another
         * without leaving the Entry tunnel
         */
        eventBus.addEventListener('switch-from-to-modal', (event) => {
          const { oldModalName, newModalName, args } = event.detail || {};
          const newModalOpenFuncName = `show${newModalName}Modal`;
          const isOldModalOpenedVarName = `is${oldModalName}ModalOpened`;

          this[isOldModalOpenedVarName] = false;

          this[newModalOpenFuncName](...(args || []));
        });

        eventBus.addEventListener('start-entry-tunnel', (event) => {
          this.showRegisterChoiceModal(event.detail.redirectUrl);
        });

        eventBus.addEventListener('initialize-login-button', () => {
          const loginButtons = document.querySelectorAll('.g_id_signin');

          if (loginButtons) {
            for (let i = 0; i < loginButtons.length; i++) {
              const loginButton = loginButtons[i];
              const loginButtonDataset = {
                ...loginButton.dataset,
                click_listener: () => {
                  clickOnGoogleButton(loginButton);
                },
              };

              loginButtonDataset.width = parseInt(window.BREAKPOINTS.isMobile ? loginButtonDataset.mobile_width : loginButtonDataset.width, 10);
              window.google?.accounts.id.renderButton(
                loginButton,
                loginButtonDataset,
              );
            }
          }
        });
      },
      updated() {
        // On mobile and tablet, if a modal is opened, add .mobile-menu-opened class on header
        // By default, .header__user-menu (modal container) is in display none without this class
        if (window.BREAKPOINTS.isMobile || window.BREAKPOINTS.isTablet) {
          this.$slots.default?.().forEach((vnode) => {
            if (vnode?.component?.proxy?.isOpened) {
              document.querySelector('.header').classList.add('mobile-menu-opened');
            }
          });
        }
      },
      methods: {
        /**
         * This method is called when closing a modal (leaving the Entry tunnel)
         */
        clear() {
          // Clear the redirect url
          this.redirectUrl = window.location.href;

          // Clear user origin
          UserOrigin.clear();
        },
        /**
         * This methods displays the login modal.
         */
        showLoginModal(event) {
          if (event) {
            const {
              email, redirectUrl, error, success,
            } = event;
            if (email) {
              this.presetEmailLogin = email;
            }
            if (redirectUrl) {
              this.redirectUrl = redirectUrl;
            }
            if (error) {
              this.presetErrorLogin = error;
            }
            if (success) {
              this.presetSuccessLogin = success;
            }
          }
          this.isLoginModalOpened = true;
        },
        /**
         * This method closes the login modal.
         */
        closeLoginModal() {
          this.presetEmailLogin = '';
          this.presetErrorLogin = '';
          this.presetSuccessLogin = '';
          this.isLoginModalOpened = false;
          this.clear();
        },
        /**
         * This methods displays the pre set login modal.
         */
        showLoginPreSetModal() {
          this.isLoginPreSetModalOpened = true;
        },
        /**
         * This method closes the pre set login modal.
         */
        closeLoginPreSetModal() {
          this.isLoginPreSetModalOpened = false;
          this.clear();
        },
        /**
         * This methods displays register-choice modal.
         */
        showRegisterChoiceModal(redirectUrl) {
          if (redirectUrl) {
            this.redirectUrl = redirectUrl;
          }
          this.isRegisterChoiceModalOpened = true;
        },
        /**
         * This method closes the register choice modal.
         */
        closeRegisterChoiceModal() {
          this.isRegisterChoiceModalOpened = false;
          this.clear();
        },
        /**
         * This method displays the registration modal.
         */
        showRegisterModal(redirectUrl) {
          UserOrigin.set(ORIGIN_SIMPLE);
          if (redirectUrl) {
            this.redirectUrl = redirectUrl;
          }
          this.isRegisterModalOpened = true;
        },
        /**
         * This method closes the register modal.
         */
        closeRegisterModal() {
          this.isRegisterModalOpened = false;
          this.clear();
        },
        /**
         * This method displays the missing-email form modal.
         */
        showMissingInformationModal() {
          this.isMissingInformationModalOpened = true;
        },
        /**
         * This method closes the missing email modal.
         */
        closeMissingInformationModal() {
          this.isMissingInformationModalOpened = false;
          this.clear();
        },
        /**
         * This method displays the delete user modal.
         */
        showDeleteModal() {
          this.isDeleteModalOpened = true;
        },
        /**
         * This method closes the delete user modal.
         */
        closeDeleteModal() {
          this.isDeleteModalOpened = false;
          this.clear();
        },
        /**
         * This method displays the delete success user modal.
         */
        showDeleteSuccessModal() {
          this.isDeleteSuccessModalOpened = true;
        },
        /**
         * This method closes the delete success user modal.
         */
        closeDeleteSuccessModal() {
          this.isDeleteSuccessModalOpened = false;
          this.clear();
        },
        /**
         * This method displays the resetting password modal.
         */
        showResettingPasswordModal() {
          this.isResettingPasswordModalOpened = true;
        },
        /**
         * This method closes the resetting password modal.
         */
        closeResettingPasswordModal() {
          this.isResettingPasswordModalOpened = false;
          this.clear();
        },
        getLoginErrorAlreadyActiveTransKey(urlParams) {
          let transkey = 'form.login.error.already_active';
          const googleActive = urlParams.get('google') === '1';
          if (googleActive) {
            transkey += '.google';
            return transkey;
          }
          return transkey;
        },
      },
    };

    createApp(component).mount(VUE_ELEMENT);
  }

  /**
   * Only init menu when the menu is ready
   */
  const storage = new Storage(LocalStorageAdapter);

  const menus = [
    {
      url: URL_HEADER_MENU,
      storageName: 'header_menu',
      wrapperSelector: '.menu-mobile-wrapper',
      isMobile: true,
    },
    {
      url: URL_HEADER_MENU_DESKTOP,
      storageName: 'header_menu_desktop',
      wrapperSelector: '.connected-user-menu--desktop',
      isMobile: false,
    },
  ];

  eventBus.addEventListener('notifications-counter-change', () => {
    menus.forEach(async (menu) => {
      try {
        const response = await axios.get(menu.url, {
          path: window.PATH,
        });
        const html = response.data;
        if (html !== storage.get(menu.storageName)) {
          document.querySelector(menu.wrapperSelector).innerHTML = html;
          // Reload click bindings
          eventBus.dispatchEvent(new CustomEvent('deobfuscate-links', { detail: menu.wrapperSelector }));
        }
        storage.set(menu.storageName, html);
      } catch (error) {
        Logger.error(error);
      }
    });
  });

  initHeader();
}
